import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux-hooks";
import { getUser } from "../../../slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { Row, Col, Card, Typography, Space, Alert } from "antd";
import {
  UserOutlined,
  NotificationOutlined,
  DashboardOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  FileTextOutlined,
  EditOutlined,
  IdcardOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const HomePageComponent: React.FC = () => {
  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);
  const userProfileInfo = useAppSelector((state) => state.auth.userProfileData);
  const userRoles = userProfileInfo?.roles || [];
  const isMobile = useIsMobile();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (basicUserInfo) {
      dispatch(getUser(basicUserInfo.id));
    }
  }, [basicUserInfo, dispatch]);

  const sections = [
    {
      title: "Users",
      description: "Add, edit, and manage system users efficiently",
      icon: <UserOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#1890ff" }} />,
      path: "/management/users",
      roles: ["ADMIN"],
    },
    {
      title: "Notifications",
      description: "Create, update, and send notifications to users",
      icon: <NotificationOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#1890ff" }} />,
      path: "/management/notifications",
      roles: ["ADMIN"],
    },
    {
      title: "Manage Changelogs",
      description: "Add, update, or remove changelog entries with ease",
      icon: <EditOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#f5222d" }} />,
      path: "/management/changelogs",
      roles: ["ADMIN"],
    },
    {
      title: "Dashboard",
      description: "View analytics, key metrics, and system overview",
      icon: <DashboardOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#1890ff" }} />,
      path: "/dashboard",
      roles: ["USER", "ADMIN"],
    },
    {
      title: "Patients",
      description:
        "Manage patient records and access their medical examination reports",
      icon: <MedicineBoxOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#faad14" }} />,
      path: "/patients",
      roles: ["USER", "ADMIN"],
    },
    {
      title: "Appointments",
      description: "Schedule, view, and manage appointments seamlessly",
      icon: <CalendarOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#faad14" }} />,
      path: "/appointments",
      roles: ["USER", "ADMIN"],
    },
    {
      title: "What's New",
      description: "Browse and review What's New entries",
      icon: <FileTextOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#f5222d" }} />,
      path: "/changelog",
      roles: ["USER", "ADMIN"],
    },
    {
      title: "Profile",
      description:
        "Update your personal details and manage clinic information easily",
      icon: <IdcardOutlined style={{ fontSize: isMobile ? 24 : 28, color: "#1890ff" }} />,
      path: "/profile",
      roles: ["USER", "ADMIN"],
    },
  ];

  const filteredSections = sections.filter((section) =>
    section.roles.some((role) => userRoles.includes(role))
  );

  const renderCard = (section: (typeof filteredSections)[0]) => (
    <Card
      hoverable
      onClick={() => navigate(section.path)}
      style={{
        textAlign: "center",
        height: "100%",
        width: "100%",
        borderRadius: isMobile ? 10 : 12,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        background: "#fff",
      }}
      styles={{
        body: {
          padding: isMobile ? "16px 14px" : "20px 12px",
        },
      }}
      onMouseEnter={(e) => {
        if (isMobile) return;
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        if (isMobile) return;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
      }}
    >
      <Space direction="vertical" size={isMobile ? 8 : 12} align="center" style={{ width: "100%" }}>
        {section.icon}
        <Title level={4} style={{ margin: 0, fontSize: isMobile ? 16 : 18 }}>
          {section.title}
        </Title>
        <Text
          type="secondary"
          style={{
            fontSize: isMobile ? 13 : 14,
            lineHeight: 1.45,
            display: "block",
          }}
        >
          {section.description}
        </Text>
      </Space>
    </Card>
  );

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1400,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: isMobile ? 16 : 24,
          margin: "0 auto",
        }}
      >
        <Alert
          message={
            isMobile
              ? "Welcome to NeoView — manage patients, appointments, and exams."
              : "Welcome to NeoView — your central hub for managing patients, appointments, and medical examinations."
          }
          type="info"
          showIcon={!isMobile}
          style={{
            background: "#f0f7ff",
            borderColor: "#d0e4ff",
            color: "#2c5282",
            borderRadius: 8,
            padding: isMobile ? "8px 12px" : "10px 14px",
            fontSize: isMobile ? 13 : 14,
          }}
        />

        <Row gutter={[isMobile ? 12 : 16, isMobile ? 12 : 16]} justify="start" style={{ margin: 0 }}>
          {filteredSections.map((section) => (
            <Col
              key={section.path}
              xs={24}
              sm={12}
              md={8}
              lg={6}
              xl={6}
              style={{ display: "flex" }}
            >
              {renderCard(section)}
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

export default HomePageComponent;
